import { getApiBaseUrl, getAuthHeaders } from '../config/api';

export interface AgentSettings {
  provider: 'OPENAI' | 'GEMINI';
  openaiModel: string;
  geminiModel: string;
  autoAnalyze: boolean;
  minimumScore: number;
  dailyLimit: number;
  analysisInstructions: string;
  calendarEnabled: boolean;
  calendarReminderMinutes: number;
  openaiConfigured?: boolean;
  openaiKeySuffix?: string | null;
  geminiConfigured?: boolean;
  geminiKeySuffix?: string | null;
  openaiApiKey?: string;
  geminiApiKey?: string;
  clearOpenaiKey?: boolean;
  clearGeminiKey?: boolean;
}

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  provider?: string;
  model?: string;
}

export interface AdherenceRow {
  criterion: string;
  requirement: string;
  evidence: string;
  adherence: 'ADHERENT' | 'PARTIAL' | 'NOT_ADHERENT' | 'UNVERIFIED';
  risk: string;
  action: string;
}

export interface AgentAnalysis {
  id: string;
  status: 'PROCESSING' | 'READY' | 'ERROR';
  provider: string;
  model: string;
  importanceScore?: number | null;
  recommendation?: 'PRIORITIZE' | 'REVIEW' | 'DISCARD' | null;
  preliminaryReport?: string | null;
  analyticalReport?: string | null;
  adherenceMatrix?: AdherenceRow[];
  productRecommendations?: Array<{ brand: string; model: string; fit: string; source: string }>;
  marketReferences?: Array<{ description: string; priceMin: number | null; priceMax: number | null; source: string; confidence: string }>;
  calendarEvents?: Array<{ title: string; date: string; time: string; description: string; type: string }>;
  errorMessage?: string | null;
  messages?: AgentMessage[];
  updatedAt: string;
}

export interface AgentOpportunity {
  id: string;
  edital: string;
  orgao: string;
  cidade: string;
  estado: string;
  objeto: string;
  modalidade: string;
  encerramento: string;
  valorTotal?: number | null;
  fonte?: string | null;
  urlEdital?: string | null;
  relevanceScore?: number | null;
  relevanceLevel?: string | null;
  analysis?: AgentAnalysis | null;
}

export interface AgentWorkspace {
  settingsReady: boolean;
  stats: { queue: number; analyzed: number; priority: number };
  opportunities: AgentOpportunity[];
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  Object.entries(getAuthHeaders()).forEach(([key, value]) => headers.set(key, value));
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${getApiBaseUrl()}${path}`, { ...init, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Falha na API (${response.status})`);
  return data as T;
}

export const opportunityAgentService = {
  getSettings: () => request<AgentSettings>('/opportunity-agent/settings'),
  saveSettings: (settings: AgentSettings) => request<{ success: boolean }>('/opportunity-agent/settings', { method: 'PUT', body: JSON.stringify(settings) }),
  testSettings: () => request<{ success: boolean; provider: string; model: string }>('/opportunity-agent/settings/test', { method: 'POST' }),
  getWorkspace: (opportunityId?: string | null) => {
    const params = new URLSearchParams({ limit: '150' });
    if (opportunityId) params.set('opportunityId', opportunityId);
    return request<AgentWorkspace>(`/opportunity-agent/opportunities?${params.toString()}`);
  },
  analyze: (externalTenderId: string) => request<AgentAnalysis>(`/opportunity-agent/analyze/${encodeURIComponent(externalTenderId)}`, { method: 'POST' }),
  analyzeBatch: () => request<{ processed: number }>('/opportunity-agent/analyze-batch', { method: 'POST' }),
  chat: (analysisId: string, message: string) => request<AgentMessage>(`/opportunity-agent/analyses/${encodeURIComponent(analysisId)}/chat`, { method: 'POST', body: JSON.stringify({ message }) }),
};
